/**
 * App-level data shapes. Screens and hooks only ever see these, never a
 * database row: both data sources (src/data/demo, src/data/supabase) map
 * their raw data into them. Names are chosen to carry over to the SwiftUI
 * app as-is (struct per interface, enum per union).
 *
 * Status unions mirror Welthra's enums on origin/main so the journey mapping
 * can be tested against every value:
 * - ApplicationStatus: supabase/migrations/20260518000006_welthra_flow_phase4_submit.sql:9-24
 *   plus 20260915000000_application_status_values.sql:18-23
 * - BlockerType: supabase/migrations/20260601000001_crm_refactor_enums.sql:84-99
 * - PolicyLifecycle: same file :72-78
 */

/** demo = fixtures; live = Welthra V2 (src/data/welthra). */
export type DataMode = "demo" | "live";

/* ------------------------------------------------------------------ */
/* Identity                                                            */
/* ------------------------------------------------------------------ */

export interface AuthUser {
  id: string;
  email: string | null;
}

export interface ClientIdentity {
  /** portal_clients.id */
  clientId: string;
  userId: string;
  leadId: string;
  orgId: string;
  status: "invited" | "active";
  firstLoginAt: string | null;
  contact: { name: string | null; email: string | null; phone: string | null; state?: string | null } | null;
  org: { name: string } | null;
}

export type ClientAccess =
  | { state: "loading"; user: null; identity: null; message?: undefined }
  | { state: "unauthenticated"; user: null; identity: null; message?: undefined }
  | { state: "no_client"; user: AuthUser; identity: null; message: string }
  | { state: "ready"; user: AuthUser; identity: ClientIdentity; message?: undefined };

export const CLIENT_ACCESS_LOADING: ClientAccess = { state: "loading", user: null, identity: null };

/* ------------------------------------------------------------------ */
/* People and appointments                                             */
/* ------------------------------------------------------------------ */

export type TeamRole = "strategist" | "coordinator";

export interface TeamMember {
  id: string;
  role: TeamRole;
  name: string;
  photoUrl: string | null;
  phone: string | null;
  email: string | null;
  npn: string | null;
}

/** How a past call went, in the client's words (V2's `outcome`); null while nobody has recorded it. */
export type CallOutcome = "showed" | "missed" | "canceled";

export type AppointmentKind = "intro" | "strategy" | "app_call" | "kickoff" | "review" | "callback" | "other";
export type AppointmentStatus = "scheduled" | "confirmed" | "completed" | "cancelled" | "no_show" | "rescheduled" | "unknown";

/**
 * Client-safe appointment. Deliberately has no field for Zoom host links,
 * passwords, closer briefs or call summaries: they must never reach a screen.
 */
export interface Appointment {
  id: string;
  kind: AppointmentKind;
  title: string;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  hostName: string | null;
  joinUrl: string | null;
  location: string | null;
  /** A past call from V2: how it went, null while unrecorded. Absent on the calls ahead and in demo. */
  outcome?: CallOutcome | null;
}

/* ------------------------------------------------------------------ */
/* Journey facts                                                       */
/* ------------------------------------------------------------------ */

export const APPLICATION_STATUSES = [
  "draft",
  "ready_to_submit",
  "submitted_to_carrier",
  "in_underwriting",
  "requirements_outstanding",
  "medical_exam_scheduled",
  "approved_as_applied",
  "approved_other_than_applied",
  "declined",
  "postponed",
  "withdrawn",
  "issued",
  "not_taken",
  "placed_in_force",
  "commission_paid",
  "cancelled_during_free_look",
  "lapsed",
  "surrendered",
  "matured",
  "claim_paid",
] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export const BLOCKER_TYPES = [
  "thinking_it_over",
  "medical_exam_needed",
  "medical_exam_scheduled",
  "telephone_interview_needed",
  "telephone_interview_scheduled",
  "documents_requested",
  "carrier_requirements_outstanding",
  "aps_pending",
  "labs_pending",
  "client_unresponsive",
  "awaiting_first_premium",
  "awaiting_carrier_decision",
  "client_traveling",
  "other",
] as const;
export type BlockerType = (typeof BLOCKER_TYPES)[number];

export const POLICY_LIFECYCLES = ["pending_first_premium", "active", "lapsed", "surrendered", "claim_paid"] as const;
export type PolicyLifecycle = (typeof POLICY_LIFECYCLES)[number];

export type IntakeStatus = "draft" | "sent" | "opened" | "submitted" | "void";
export type PresentationStatus = "draft" | "intake_complete" | "generating" | "ready_to_present" | "presented" | "updated_after_presentation";

export interface Requirement {
  id: string;
  type: BlockerType;
  resolved: boolean;
  /** When the exam or interview is booked for. */
  scheduledAt: string | null;
  dueAt: string | null;
  resolvedAt: string | null;
  /** Client-safe checklist, e.g. the documents the carrier asked for. */
  items?: { label: string; done: boolean }[];
}

export interface OfferTerms {
  healthClass: string;
  monthlyPremium: number | null;
  deathBenefit: number | null;
}

export interface Counteroffer {
  applied: OfferTerms;
  offered: OfferTerms;
  /** Plain-English summary of what changed, written by the strategist. */
  summary: string;
  respondBy: string | null;
}

export interface ApplicationFacts {
  id: string;
  status: ApplicationStatus;
  statusChangedAt: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  issuedAt: string | null;
  placedAt: string | null;
  postponedUntil: string | null;
  carrierName: string | null;
  productName: string | null;
  counteroffer: Counteroffer | null;
}

export interface PolicyFacts {
  lifecycle: PolicyLifecycle;
  issuedAt: string | null;
  effectiveDate: string | null;
  firstPremiumDueAt: string | null;
  firstPremiumPaidAt: string | null;
}

/** One step of her journey as Welthra V2 has it (GET /client/journey): its own key, title and state. */
export interface ServerJourneyStep {
  key: string;
  title: string;
  state: "done" | "current" | "upcoming";
  /** When it happened (done); on next_checkup, her next booked review. */
  at: string | null;
}

/** Her journey as Welthra V2 has it. The app renders `steps` as given: no fixed count or index. */
export interface ServerJourney {
  /**
   * 6c.1: a lead (never sat with a strategist) sees no steps and no stage words at all; a client
   * sees the journey. Absent from an older server: treated as "client", as before.
   */
  audience?: "lead" | "client";
  step: string | null;
  steps: ServerJourneyStep[];
  /** She missed or canceled a call and has nothing booked: "Let's find a new time", with her booking link when there is one. */
  recover: { title: string; bookUrl: string | null } | null;
  /** The published plan's next check-up (YYYY-MM-DD), or null. */
  nextCheckupDue: string | null;
}

export interface JourneyFacts {
  /** contacts.stage: the coarse fallback when finer facts are hidden. */
  contactStage: string | null;
  appointments: Appointment[];
  presentation: { status: PresentationStatus; presentedAt: string | null } | null;
  intake: { status: IntakeStatus; submittedAt: string | null } | null;
  application: ApplicationFacts | null;
  requirements: Requirement[];
  policy: PolicyFacts | null;
  /**
   * What the backend let the client read. When a part is hidden the journey
   * says "your strategist keeps this updated" instead of guessing.
   */
  visibility: { application: boolean; requirements: boolean; intake: boolean };
  /** Live: the journey as Welthra V2 has it, rendered as given where the app shows steps. Absent in demo personas without one. */
  server?: ServerJourney | null;
  /** 6c.1: "lead" hides every step and stage word; absent means "client" (today's app). */
  audience?: "lead" | "client";
}

/* ------------------------------------------------------------------ */
/* Plan, policies, documents                                           */
/* ------------------------------------------------------------------ */

/** Written by Welthra's generate-presentation function into presentations.generated_next_steps. */
export interface ThreeBucketAnalysis {
  current_taxable_percent: number;
  current_tax_deferred_percent: number;
  current_tax_free_percent: number;
  current_taxable_amount: number;
  current_tax_deferred_amount: number;
  current_tax_free_amount: number;
  gap_analysis?: string;
}

export interface BucketRecommendation {
  age_bracket?: string;
  recommended_taxable: number;
  recommended_tax_deferred: number;
  recommended_tax_free: number;
}

/** The newest presentation shared to the client, flattened for screens. */
export interface SharedPresentation {
  id: string;
  type: string;
  created_at: string | null;
  age: number | null;
  desired_retirement_age: number | null;
  desired_monthly_retirement_income: number | null;
  retirement_savings_total: number | null;
  annual_household_income: number | null;
  monthly_free_cash_flow: number | null;
  strategy: { summary?: string; recommendations?: unknown[] } | null;
  gap: { summary?: string } | null;
  snapshot: { summary?: string } | null;
  buckets: ThreeBucketAnalysis | null;
  bucketTarget: BucketRecommendation | null;
  nextSteps: string[];
}

export interface PolicyClientSummary {
  policy_id: string;
  reason_for_product: string | null;
  next_payment_date: string | null;
  payment_frequency: "monthly" | "quarterly" | "semi_annual" | "annual" | "single" | null;
  projected_income_monthly_cents: number | null;
  projected_cash_value_cents: number | null;
  projected_at_age: number | null;
  carrier_portal_url: string | null;
}

/** Explicit fields only: never the whole policies row, which carries commission data. */
export interface ClientPolicy {
  id: string;
  carrier: string;
  product_type: string;
  product_name?: string | null;
  policy_number: string | null;
  premium_cents: number | null;
  premium_amount: number;
  face_amount_cents: number | null;
  lifecycle_status: string;
  status: string;
  effective_date: string | null;
  paid_through_date: string | null;
  first_premium_paid_at: string | null;
  issued_at: string | null;
  term_months: number | null;
  summary: PolicyClientSummary | null;
  /** 6c.2: Welthra V2's view of the policy (the client-view stage track, details, files, its place in the plan). */
  view?: PolicyView;
}

/** One client-view stage (M35-CONTRACT 6c.2): never the desk's words. */
export interface PolicyStage {
  key: string;
  title: string;
  state: "done" | "current" | "upcoming";
  at: string | null;
}

/** GET /client/policies, one policy as Welthra V2 sends it (M35-CONTRACT 6c.2). */
export interface PolicyView {
  id: string;
  carrier: { name: string };
  product: { name: string; kind: "iul" | "fia" | "myga" | "whole_life" | "term" | "other" };
  contractNumber: string | null;
  stage: { key: string; title: string; detail: string | null; at: string | null };
  stages: PolicyStage[];
  premium: { cents: number; mode: string | null } | null;
  dates: { submittedAt: string | null; approvedAt: string | null; issuedAt: string | null; placedAt: string | null; freeLookEndsAt: string | null };
  details: { label: string; value: string }[];
  files: { id: string; name: string; kind: string }[];
  plan: { optionId: string | null; bucket: "safe" | "flexible" | "growth" | null; why: string | null } | null;
}

/** GET /client/numbers (M35-CONTRACT 6c.4): her survey answers, laid out by the server; rendered as given. */
export interface StatedNumbers {
  title: string;
  subtitle: string;
  survey: { v: number; at: string; complete: boolean } | null;
  groups: { key: string; title: string; rows: { label: string; value: string; note: string | null }[] }[];
}

/** POST /client/activity (M35-CONTRACT 6c.3): the screens she opens. */
export type ActivityScreen = "home" | "plan" | "plan_published" | "numbers" | "policies" | "policy" | "documents" | "messages" | "calls" | "learn" | "faq" | "profile";
export interface ActivityEvent {
  screen: ActivityScreen;
  at: string;
  ms?: number;
}

/* ------------------------------------------------------------------ */
/* Plan details                                                        */
/* ------------------------------------------------------------------ */

export interface CarrierRatings {
  amBest: string | null;
  sp: string | null;
  moodys: string | null;
  fitch: string | null;
  /** AM Best's word for the grade, e.g. "Superior". */
  label: string | null;
  asOf: string | null;
}

export interface CarrierProfile {
  slug: string | null;
  name: string;
  legalName: string | null;
  ratings: CarrierRatings | null;
  /** The company page on taxfreewealthplan.com. */
  profileUrl: string | null;
}

export type ProductKind = "iul" | "fia" | "myga" | "spia" | "wl" | "term" | "other";

export interface PlanProduct {
  id: string;
  kind: ProductKind;
  name: string;
  carrier: CarrierProfile | null;
  monthlyPremium: number | null;
  singlePremium: number | null;
  /** Years of premium. */
  payYears: number | null;
  deathBenefit: number | null;
  illustratedMonthlyIncome: number | null;
  incomeStartAge: number | null;
  /** Why it is in the plan, in the strategist's words. */
  role: string | null;
  /** The comparison-tool slug on the website, for annuities. */
  librarySlug?: string | null;
}

/** Inputs for the year-by-year chart (src/domain/illustration.ts). */
export interface IllustrationSpec {
  issueAge: number;
  monthlyPremium: number;
  payYears: number;
  face: number;
  rate: number;
  lowRate: number;
  incomeStartAge: number;
  monthlyIncome: number;
}

export interface PlanDetails {
  products: PlanProduct[];
  illustration: IllustrationSpec | null;
  /** The plan her strategist published (Welthra V2 M35); null until one is. Absent in demo personas without one. */
  published?: PublishedPlan | null;
}

/* ------------------------------------------------------------------ */
/* The published plan (Welthra V2 M35, PlanResult v1)                  */
/* ------------------------------------------------------------------ */

/**
 * A frozen snapshot: the app shows its figures exactly as published and works
 * none of them out. Money is whole dollars, today's unless a field says
 * nominal; percents are 0 to 100. A section the snapshot leaves out is not
 * shown, so even a field Welthra requires is optional here.
 * Contract: ~/workspace/welthra-v2-previews/M35-CONTRACT.md, sections 4 and 5.
 */
export interface PublishedPlan {
  id: string;
  version: number;
  publishedAt: string;
  publishedBy: { name: string | null; photoUrl: string | null } | null;
  /** Version 2 or later: "Updated" on the plan. */
  updated: boolean;
  /** The next check-up, YYYY-MM-DD. */
  nextReviewAt: string | null;
  nextSteps: { id?: string | null; text: string }[];
  /** A line to her from the strategist. */
  note: string | null;
  plan: PlanResult;
}

export type BucketKey = "flexible" | "safe" | "growth";
export type BucketSplit = Record<BucketKey, number>;

export interface PlanVerdict {
  verdict: "recommended" | "not_recommended" | "not_tested";
  lineText?: string | null;
  years?: number | null;
  startYear?: number | null;
  premium?: number | null;
  startAge?: number | null;
  why?: string | null;
}

export interface PlanResult {
  v?: number;
  asOf?: string | null;
  startYear?: number | null;
  household?: { married?: boolean | null; state?: string | null; people?: { id: string; name?: string | null; age?: number | null; retired?: boolean | null }[] } | null;
  budget?: {
    lines?: { label: string; monthly: number; endsYear?: number | null; kind?: "bill" | "on_top" | null }[] | null;
    billsMonthly: number;
    onTopMonthly: number;
    goalMonthly: number;
    goalScale?: number | null;
  } | null;
  income?: {
    year?: number | null;
    lines?: { label: string; kind?: string | null; monthly: number }[] | null;
    beforeTaxMonthly?: number | null;
    taxMonthly?: number | null;
    afterTaxMonthly: number;
  } | null;
  gap?: { goalMonthly: number; incomeAfterTaxMonthly: number; gapMonthly: number; planPaysMonthly: number; currentPaysMonthly?: number | null; met: boolean } | null;
  buckets?: { year?: number | null; now: BucketSplit; plan: BucketSplit; current?: BucketSplit | null; targetPct?: BucketSplit | null; stageLabel?: string | null; flexibleMonths?: number | null } | null;
  moves?: { lever?: string | null; text: string; amount?: number | null; bucket?: BucketKey | null; year?: number | null }[] | null;
  tax?: {
    year?: number | null;
    federal?: number | null;
    state?: number | null;
    total?: number | null;
    bracketPct?: number | null;
    effectivePct?: number | null;
    irmaaTier?: number | null;
    irmaaHeadroom?: number | null;
    lifetime?: { savedToday?: number | null } | null;
    rmd?: { firstYear?: number | null; first?: number | null; peak?: number | null; peakYear?: number | null; total?: number | null } | null;
    roth?: PlanVerdict | null;
    qlac?: PlanVerdict | null;
  } | null;
  projection?: {
    dollars?: "nominal" | string | null;
    rows: { year: number; ageA?: number | null; ageB?: number | null; goal?: number | null; income?: number | null; flexible: number; safe: number; growth: number; total: number }[];
  } | null;
  solutions?: {
    id: string;
    productKey?: string | null;
    productName: string;
    carrier?: string | null;
    type?: string | null;
    bucket?: BucketKey | null;
    amount?: number | null;
    incomeMonthly?: number | null;
    incomeStartAge?: number | null;
    why?: string | null;
    planOptionId?: string | null;
  }[] | null;
  nextSteps?: { id?: string | null; text: string }[] | null;
  nextReviewAt?: string | null;
  estimateNote?: string | null;
}

export interface ApplicationSummary {
  submittedAt: string | null;
  reference: string | null;
  sections: { title: string; rows: { label: string; value: string; sensitive?: boolean }[] }[];
}

export type DocumentType = "illustration" | "application" | "policy" | "other";

export interface ClientDocument {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  created_at: string;
  document_type: DocumentType | null;
}

/* ------------------------------------------------------------------ */
/* Messages and FAQ                                                    */
/* ------------------------------------------------------------------ */

/** Who on the team wrote a message (V2's `author`); `system` is the firm itself (reminders, the first-contact texts). */
export interface MessageAuthor {
  name: string;
  role: "strategist" | "coordinator" | "system";
  photoUrl: string | null;
}

export interface ThreadMessage {
  id: string;
  content: string;
  /** inbound = from the client, outbound = from the team. */
  direction: "inbound" | "outbound";
  message_type: string;
  created_at: string;
  status: string | null;
  /** Local-only: sent from this device and not yet confirmed. */
  pending?: boolean;
  /** The team's messages: who wrote it. null on hers; absent where the backend does not say. */
  author?: MessageAuthor | null;
}

export type FaqAudience = "prospect" | "client" | "all";

export interface FaqItem {
  id: string;
  question: string;
  answer_md: string;
  video_url: string | null;
  audience: FaqAudience;
  sort_order: number;
}
